/**
 * `AccountRow` - one `<tr>` in the accounts list.
 *
 * Clicking the row (or pressing Enter / Space while focused) navigates
 * to `/members/:id`. Up/Down arrow keys move focus between rows; Esc
 * returns focus to the FilterBar. These behaviours are owned at the
 * `AccountTable` level (the row exposes `onActivate` and `data-row-index`
 * so the table can wire them).
 *
 * The full SM2 id is exposed via `data-account-id` to support the
 * keyboard-navigation contract and per-row assertions in tests, and so
 * that the route navigation does not have to re-derive it from the
 * shortened cell text.
 */

import { useCallback } from 'react'
import type { KeyboardEvent } from 'react'

import type { Account } from '../../lib/bff-client'
import { ACCOUNT_COLUMNS } from './columns'

export interface AccountRowProps {
  readonly account: Account
  readonly rowIndex: number
  readonly onActivate: (account: Account) => void
  readonly onArrow: (direction: 1 | -1, currentIndex: number) => void
  readonly onEscape: () => void
}

export function AccountRow({
  account,
  rowIndex,
  onActivate,
  onArrow,
  onEscape,
}: AccountRowProps): JSX.Element {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTableRowElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onActivate(account)
        return
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        onArrow(1, rowIndex)
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        onArrow(-1, rowIndex)
        return
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        onEscape()
      }
    },
    [account, rowIndex, onActivate, onArrow, onEscape],
  )

  return (
    <tr
      data-account-id={account.id}
      data-row-index={rowIndex}
      tabIndex={0}
      role="row"
      aria-label={`Account ${account.ext_account_id}, status ${account.status}`}
      onClick={() => onActivate(account)}
      onKeyDown={handleKeyDown}
      className="cursor-pointer border-b border-border-divider bg-bg-surface hover:bg-bg-surface-2 focus:bg-bg-surface-2"
    >
      {ACCOUNT_COLUMNS.map((col) => (
        <td
          key={col.key}
          role="cell"
          // 9px/12px cell padding per the design handoff table spec - denser
          // than the 8px/12px it replaced, which is the point of a tool table.
          className={`px-[12px] py-[9px] ${col.className ?? ''}`}
        >
          {col.render(account)}
        </td>
      ))}
    </tr>
  )
}
